import type { Feature, FeatureCollection, Point } from 'geojson';
import type { SymbolLayerSpecification } from 'mapbox-gl';
import React, { useEffect, useMemo } from 'react';
import { Layer, Source, useMap } from 'react-map-gl';

import dropImg from '@assets/drop.png';
import pickImg from '@assets/pick.png';
import { EMap } from '@enums/map.enum';
import { StopType } from '@enums/trips.enum';
import type { TripStop } from '@store/trips/tripsSlice';

type Props = {
	markers: TripStop[];
};

const MapMarkers: React.FC<Props> = ({ markers }) => {
	const { current: map } = useMap();

	const markerFeatures = useMemo<FeatureCollection<Point>>(() => {
		const features: Feature<Point>[] = markers.map((marker) => {
			const firstPassenger = marker.passengers?.[0];
			const stopType = firstPassenger?.stopType;
			const isDrop = stopType === StopType.Drop;
			const iconName = isDrop ? StopType.Drop : StopType.Pick;

			return {
				type: EMap.Feature,
				id: marker.id,
				geometry: {
					type: EMap.Point,
					coordinates: [marker.address.lng, marker.address.lat],
				},
				properties: {
					icon: iconName,
				},
			};
		});

		return {
			type: EMap.FeatureCollection,
			features,
		};
	}, [markers]);

	const symbolLayerStyle: SymbolLayerSpecification = {
		id: EMap.markerSymbols,
		type: EMap.symbol,
		source: EMap.placeholder,
		layout: {
			[EMap.iconImage]: [EMap.get, EMap.icon],
			[EMap.iconAllowOverlap]: true,
			[EMap.iconIgnorePlacement]: true,
			[EMap.iconSize]: 0.5,
		},
	};

	useEffect(() => {
		if (!map) {
			return;
		}

		const iconUrls: Record<string, string> = {
			[StopType.Pick]: pickImg,
			[StopType.Drop]: dropImg,
		};

		const addImages = () => {
			Object.entries(iconUrls).forEach(([id, url]) => {
				if (map.hasImage(id)) {
					return;
				}
				const img = new Image();
				img.src = url;
				img.onload = () => {
					if (!map.hasImage(id)) {
						map.addImage(id, img);
					}
				};
			});
		};

		if (map.isStyleLoaded()) {
			addImages();
		}
		map.on(EMap.styleLoad, addImages);

		return () => {
			map.off(EMap.styleLoad, addImages);
		};
	}, [map]);

	return (
		<Source id={EMap.markerSymbols} type={EMap.geojson} data={markerFeatures}>
			<Layer {...symbolLayerStyle} />
		</Source>
	);
};

export default MapMarkers;
